import { UserIdentity } from './identity';
import { UserProfile, getProfile, saveProfile } from './metadata';

const IDENTITY_KEY = 'authenticulture_identity';
const PROFILE_CREATED_KEY = 'authenticulture_profile_created';
const BASE_URL = 'https://authenticulture.net';

export interface TransferData {
  identity: UserIdentity;
  profile: UserProfile | null;
}

function encode(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...Array.from(new Uint8Array(buf))));
}

function decode(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function deriveKey(passphrase: string, salt: Uint8Array, usage: KeyUsage[]): Promise<CryptoKey> {
  const keyMaterial = await crypto.subtle.importKey(
    'raw', new TextEncoder().encode(passphrase), 'PBKDF2', false, ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: 200000, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    usage
  );
}

export async function exportTransferCode(passphrase: string): Promise<string> {
  const stored = localStorage.getItem(IDENTITY_KEY);
  if (!stored) throw new Error('No identity to export');
  const payload: TransferData = { identity: JSON.parse(stored), profile: getProfile() };
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt, ['encrypt']);
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(JSON.stringify(payload))
  );
  return `v3:${encode(salt.buffer)}:${encode(iv.buffer)}:${encode(ciphertext)}`;
}

export function buildTransferUrl(code: string): string {
  return `${BASE_URL}/#import:${code}`;
}

export function parseImportHash(): string | null {
  const hash = window.location.hash;
  if (!hash.startsWith('#import:')) return null;
  return hash.slice('#import:'.length);
}

export async function validateTransferCode(code: string, passphrase: string): Promise<TransferData | null> {
  try {
    if (!code.startsWith('v3:')) return null;
    const parts = code.split(':');
    if (parts.length < 4) return null;
    const [, saltB64, ivB64, ...rest] = parts;
    const ciphertextB64 = rest.join(':');
    const salt = decode(saltB64);
    const iv = decode(ivB64);
    const ciphertext = decode(ciphertextB64);
    const key = await deriveKey(passphrase, salt, ['decrypt']);
    const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
    const data = JSON.parse(new TextDecoder().decode(plaintext)) as TransferData;
    const identity = data.identity;
    if (!identity?.publicKey || !identity?.privateKey || !identity?.userId) return null;
    return { identity, profile: data.profile ?? null };
  } catch {
    return null;
  }
}

export function importTransferData({ identity, profile }: TransferData): void {
  localStorage.setItem(IDENTITY_KEY, JSON.stringify(identity));
  if (profile) {
    saveProfile(profile);
    localStorage.setItem(PROFILE_CREATED_KEY, 'true');
  } else if (!getProfile()) {
    localStorage.removeItem(PROFILE_CREATED_KEY);
  }
  window.history.replaceState(null, '', window.location.pathname);
}
